/**
 * Kiosk durumunun **şekli** ve okuma kapısı. Uygulaması `kiosk-store.tsx`'te.
 *
 * Ayrı dosyada olmasının nedeni React değil Vite (#143): bir modül hem bileşen hem bileşen
 * olmayan bir şey dışa aktarırsa Fast Refresh çalışmaz, modül `invalidate` olur ve onu içe
 * aktaran herkes yeniden çalışır. Yeniden çalışan modül `createContext`'i tekrar çağırır;
 * iki güncelleme üst üste binerse sağlayıcı ile tüketici **farklı** context nesnesi tutar ve
 * aşağıdaki korkuluk boş yere fırlar. Bileşen (`KioskProvider`) orada, bileşen olmayan
 * (`KioskContext`, `useKiosk`) burada durduğu sürece bu olamaz.
 */

import { createContext, use } from 'react'
import type { OutfitId } from '../outfits'
import type { CharacterStatus, CreatedCharacter, ShowcaseCharacter } from '../api/client'
import type { Region } from '../camera/useCamera'

export interface Capture {
  blob: Blob
  /** `URL.createObjectURL` — önizleme için; oturum sıfırlanınca serbest bırakılır. */
  url: string
  /**
   * Kılavuz ovalinin ham karedeki yeri (#98). `null` → kadraj ölçülemedi; sunucu tam kareyle
   * çalışır, yani bugünkü davranış. Fotoğraf her hâlükârda **tam** gönderilir.
   */
  region: Region | null
}

interface KioskState {
  eventId: string | null
  capture: Capture | null
  displayName: string
  /** Seçilmeden üretim başlamaz: sessiz varsayılan forma yok (#27). */
  outfitId: OutfitId | null
  submitting: boolean
  submitError: string | null
  created: CreatedCharacter | null
  status: CharacterStatus | null
  failureReason: string | null
  previewUrl: string | null
  collection: ShowcaseCharacter[]
  newestCharacterId: string | null
  streamConnected: boolean
}

interface KioskActions {
  setCapture: (capture: Capture) => void
  setDisplayName: (name: string) => void
  setOutfitId: (outfit: OutfitId) => void
  /** Foto + isim + stil → `POST /api/characters`. Başarılıysa takip QR'ı hazırdır. */
  submit: () => Promise<CreatedCharacter | null>
  /** Yeni misafir için oturumu temizler (koleksiyon kalır). */
  reset: () => void
  /**
   * Misafir kendini beğenmedi (#94). Kart **önce** gridden düşer — kiosk 60 saniyelik bir
   * yüzey, misafir dokunuşun sonucunu beklememeli. Sunucu reddederse liste geri yüklenir ve
   * `false` döner; çağıran yüzey o zaman misafire söyler.
   */
  removeCharacter: (characterId: string) => Promise<boolean>
}

export const KioskContext = createContext<(KioskState & KioskActions) | null>(null)

export function useKiosk() {
  const value = use(KioskContext)
  if (!value) throw new Error('useKiosk yalnız <KioskProvider> içinde çağrılır')
  return value
}
